import {
  BetaFeedbackApiError,
  FEEDBACK_SCREENSHOT_MAX_BYTES,
  type FeedbackStorageClientLike,
  isFeedbackScreenshotMimeType,
  uploadBetaFeedbackScreenshot,
  useSubmitBetaFeedback,
} from '@sazeo/sdk';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Toast,
  ToastDescription,
  ToastProvider,
  ToastTitle,
  ToastViewport,
  cn,
} from '@sazeo/ui';
import { MessageSquarePlus, Paperclip } from 'lucide-react';
import { type FormEvent, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRouteLoaderData } from 'react-router-dom';

import type { RootLoaderData } from '../App';
import { tenantSdkClient } from '../lib/sdk';

type ToastState = {
  variant: 'success' | 'error';
  title: string;
  description?: string;
};

function errorKey(error: unknown): string {
  if (error instanceof BetaFeedbackApiError) {
    if (error.code === 'rate_limited') return 'error_rate_limited';
    if (error.code === 'invalid_screenshot_type') return 'error_screenshot_type';
    if (error.code === 'screenshot_too_large') return 'error_screenshot_size';
  }
  return 'error_submit';
}

export function BetaFeedbackButton() {
  const data = useRouteLoaderData('root') as RootLoaderData | undefined;
  const { t } = useTranslation('core.feedback');
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [toast, setToast] = useState<ToastState | null>(null);
  const mutation = useSubmitBetaFeedback(tenantSdkClient, data?.tenant.id);

  const canSubmit = useMemo(
    () => message.trim().length > 0 && message.trim().length <= 4000 && !fileError,
    [fileError, message],
  );

  function resetForm() {
    setMessage('');
    setFile(null);
    setFileError(null);
  }

  function handleFile(nextFile: File | null) {
    setFileError(null);
    if (!nextFile) {
      setFile(null);
      return;
    }
    if (!isFeedbackScreenshotMimeType(nextFile.type)) {
      setFile(null);
      setFileError(t('error_screenshot_type'));
      return;
    }
    if (nextFile.size > FEEDBACK_SCREENSHOT_MAX_BYTES) {
      setFile(null);
      setFileError(t('error_screenshot_size'));
      return;
    }
    setFile(nextFile);
  }

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!data || !canSubmit) return;

    setUploading(true);
    try {
      const screenshotPath = file
        ? await uploadBetaFeedbackScreenshot(tenantSdkClient as FeedbackStorageClientLike, {
            tenantId: data.tenant.id,
            userId: data.user.id,
            file,
          })
        : null;

      await mutation.mutateAsync({
        message: message.trim(),
        page_url: typeof window !== 'undefined' ? window.location.href : null,
        screenshot_path: screenshotPath,
      });
      setToast({ variant: 'success', title: t('success_title'), description: t('success_body') });
      resetForm();
      setOpen(false);
    } catch (err) {
      setToast({
        variant: 'error',
        title: t(errorKey(err)),
        description:
          err instanceof Error && !(err instanceof BetaFeedbackApiError) ? err.message : undefined,
      });
    } finally {
      setUploading(false);
    }
  }

  return (
    <ToastProvider>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button
            type="button"
            variant="primary"
            className="fixed bottom-5 right-5 z-40 shadow-[var(--shadow-lg)]"
            leftIcon={<MessageSquarePlus size={16} aria-hidden />}
          >
            {t('trigger')}
          </Button>
        </DialogTrigger>
        <DialogContent className="max-w-[520px]">
          <form onSubmit={(event) => void onSubmit(event)} noValidate>
            <DialogHeader>
              <DialogTitle>{t('title')}</DialogTitle>
              <DialogDescription>{t('description')}</DialogDescription>
            </DialogHeader>

            <div className="mt-4 flex flex-col gap-3">
              <label htmlFor="beta-feedback-message" className="flex flex-col gap-1.5">
                <span className="text-[12px] font-medium text-[var(--color-text-secondary)]">
                  {t('message_label')}
                </span>
                <textarea
                  id="beta-feedback-message"
                  value={message}
                  onChange={(event) => setMessage(event.target.value)}
                  maxLength={4000}
                  rows={6}
                  className={cn(
                    'min-h-[140px] w-full resize-y rounded-[4px] border border-[var(--color-border)]',
                    'bg-[var(--color-bg)] px-3 py-2 text-[14px] text-[var(--color-text-primary)]',
                    'placeholder:text-[var(--color-text-tertiary)]',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)]',
                  )}
                  placeholder={t('message_placeholder')}
                />
              </label>

              <div className="flex items-center justify-between gap-3 text-[12px] text-[var(--color-text-tertiary)]">
                <span>{t('page_hint')}</span>
                <span>{t('char_count', { count: message.length })}</span>
              </div>

              <label
                htmlFor="beta-feedback-screenshot"
                className="flex cursor-pointer items-center justify-between gap-3 rounded-[4px] border border-dashed border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-[12px] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg)]"
              >
                <span className="inline-flex min-w-0 items-center gap-2">
                  <Paperclip size={14} aria-hidden />
                  <span className="truncate">{file ? file.name : t('screenshot_label')}</span>
                </span>
                <span className="shrink-0 text-[var(--color-text-tertiary)]">
                  {t('screenshot_hint')}
                </span>
                <input
                  id="beta-feedback-screenshot"
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  className="sr-only"
                  onChange={(event) => handleFile(event.target.files?.[0] ?? null)}
                />
              </label>
              {fileError ? (
                <p className="text-[12px] text-[var(--color-error)]">{fileError}</p>
              ) : null}
            </div>

            <DialogFooter className="mt-5">
              <Button type="button" variant="secondary" onClick={() => setOpen(false)}>
                {t('cancel')}
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={!canSubmit}
                loading={mutation.isPending || uploading}
                loadingLabel={t('sending')}
              >
                {t('submit')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {toast ? (
        <Toast
          variant={toast.variant}
          open
          onOpenChange={(nextOpen) => {
            if (!nextOpen) setToast(null);
          }}
        >
          <div className="flex flex-col gap-1">
            <ToastTitle>{toast.title}</ToastTitle>
            {toast.description ? <ToastDescription>{toast.description}</ToastDescription> : null}
          </div>
        </Toast>
      ) : null}
      <ToastViewport />
    </ToastProvider>
  );
}
